import {
  DASHBOARD_WINDOWS,
  type DashboardWindowDays,
  parseDashboardWindow,
} from "@sauron/contracts"
import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { ArrowRight } from "lucide-react"
import { Link, useSearchParams } from "react-router-dom"
import { api, type DashboardReason, type DashboardSnapshot, type Registry } from "@/api.js"
import { Async } from "@/components/Async.js"
import { PageHeader, Section, Stat, StatRow } from "@/components/Page.js"
import { plural } from "@/lib/plural.js"
import { cn } from "@/lib/utils.js"
import { DailyChart } from "./dashboard/DailyChart.js"
import { count, duration, share } from "./dashboard/format.js"
import { RankedBars, type RankedRow } from "./dashboard/RankedBars.js"
import { Sources } from "./dashboard/Sources.js"
import { Verdict } from "./Health.js"
import { SEVERITY } from "./health/Source.js"

/**
 * The Pulpit — the panel's front page, asked for by the product owner on
 * 2026-10-05: what the gate has been answering, and why, over a window of
 * days.
 *
 * **Everything on it is the audit log's, and everything leads back to it.**
 * Each figure is a count of decisions the core recorded, and each row of each
 * list is a link to the decisions it counts — a dashboard nobody can act on is
 * the one CLAUDE.md refused, and the way this one avoids being it is that
 * every number is a door. The sources' own view of themselves stays on the
 * health screen; here they appear only as the calls they were asked.
 */
export function DashboardScreen() {
  const [params, setParams] = useSearchParams()
  const days = parseDashboardWindow(params.get("days") ?? undefined)

  const state = useQuery({
    queryKey: ["dashboard", days],
    queryFn: () => api.dashboard(days),
    refetchInterval: 60_000,
    // A new window holds the old picture, dimmed, until its own arrives — no
    // flash of an empty page, no layout jump.
    placeholderData: keepPreviousData,
  })
  const health = useQuery({ queryKey: ["health"], queryFn: api.health, refetchInterval: 30_000 })
  const registry = useQuery({ queryKey: ["registry"], queryFn: api.registry })

  function choose(next: DashboardWindowDays) {
    setParams(
      (previous) => {
        const updated = new URLSearchParams(previous)
        updated.set("days", String(next))
        return updated
      },
      { replace: true },
    )
  }

  return (
    <div className="space-y-8">
      <PageHeader title="Pulpit" actions={<WindowSwitch value={days} onChange={choose} />} />

      {health.data !== undefined && (
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
          <Verdict
            sources={[...health.data.providers].sort(
              (left, right) => SEVERITY[left.status] - SEVERITY[right.status],
            )}
          />
          <Link
            to="/health"
            className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground no-underline transition-colors hover:text-foreground"
          >
            Stan systemu
            <ArrowRight className="size-3.5" />
          </Link>
        </div>
      )}

      <Async state={state}>
        {(snapshot) => (
          <div
            className={cn(
              "space-y-9 transition-opacity",
              state.isPlaceholderData && "pointer-events-none opacity-60",
            )}
          >
            <Scoreboard snapshot={snapshot} />

            {snapshot.totals.decisions === 0 ? (
              <p className="rounded-lg border bg-card px-5 py-10 text-center text-sm text-muted-foreground shadow-xs">
                W tym okresie nie zapadła żadna decyzja.
              </p>
            ) : (
              <DailyChart days={snapshot.daily} />
            )}

            <div className="grid gap-x-8 gap-y-9 lg:grid-cols-2">
              <Section title="Dlaczego przedpłata">
                <RankedBars
                  tone="prepay"
                  rows={whyPrepayment(snapshot, registry.data)}
                  empty="Żadnej przedpłaty w tym okresie."
                />
              </Section>
              <Section title="Co znalazły reguły punktowe">
                <RankedBars
                  tone="neutral"
                  rows={findings(snapshot, registry.data)}
                  empty="Reguły punktowe niczego nie znalazły."
                />
              </Section>
            </div>

            <Section title="Źródła danych">
              <Sources providers={snapshot.providers} />
            </Section>

            <Section title="Kto pytał">
              <div className="max-w-xl">
                <RankedBars
                  tone="neutral"
                  rows={snapshot.apiClients.map((client) => ({
                    key: client.apiClientId,
                    label: client.name ?? client.apiClientId,
                    // The identifier beneath a name; a caller with no name is its identifier already.
                    detail:
                      client.name === null ? null : (
                        <span className="font-mono">{client.apiClientId}</span>
                      ),
                    value: client.decisions,
                    share: share(client.decisions, snapshot.totals.decisions),
                    to: `/decisions?apiClientId=${encodeURIComponent(client.apiClientId)}`,
                  }))}
                  empty="Nikt nie pytał w tym okresie."
                />
              </div>
            </Section>
          </div>
        )}
      </Async>
    </div>
  )
}

/**
 * Five figures on one surface. Each compares itself with the window of the
 * same length just before — a number with nothing beside it cannot say
 * whether today is unusual.
 */
function Scoreboard({ snapshot }: { snapshot: DashboardSnapshot }) {
  const { totals, previous, window } = snapshot
  const before = (value: string | null) =>
    previous.decisions === 0 ? "poprzednio brak decyzji" : `poprzednio ${value ?? "—"}`

  return (
    <StatRow>
      <Stat
        label="Decyzje"
        value={count(totals.decisions)}
        hint={`Odpowiedzi z ${window.days} dni, licząc z dzisiejszym. „Poprzednio” to tyle samo dni tuż przed nimi.`}
        detail={before(count(previous.decisions))}
      />
      <Stat
        label="Podmioty"
        value={count(totals.taxpayers)}
        hint="Odrębni podatnicy, o których padło pytanie."
        detail={before(count(previous.taxpayers))}
      />
      <Stat
        label="Przedpłata"
        value={<Part part={totals.prepayment} whole={totals.decisions} />}
        tone="prepay"
        hint="Sprzedaż doszła do skutku, tylko z góry."
        detail={before(share(previous.prepayment, previous.decisions))}
      />
      <Stat
        label="Niepewne"
        value={<Part part={totals.degraded} whole={totals.decisions} />}
        tone={totals.degraded > 0 ? "critical" : "neutral"}
        hint="Odpowiedzi z fail-closed, nie z kompletnych danych."
        detail={before(share(previous.degraded, previous.decisions))}
      />
      <Stat
        label="Czas odpowiedzi"
        value={duration(totals.latencyP95Ms)}
        hint="95% decyzji zapadło szybciej. Whitelabel czeka 5 s."
        detail={`mediana ${duration(totals.latencyP50Ms)}`}
      />
    </StatRow>
  )
}

/** A count with its share beside it: the number is the fact, the share is scale. */
function Part({ part, whole }: { part: number; whole: number }) {
  const ratio = share(part, whole)
  return (
    <>
      {count(part)}
      {ratio !== null && (
        <span className="ml-1.5 text-[15px] font-normal text-muted-foreground">· {ratio}</span>
      )}
    </>
  )
}

/**
 * The window as three choices side by side — real radio inputs under the
 * styling, so the keyboard and a screen reader get a radio group for free.
 */
function WindowSwitch({
  value,
  onChange,
}: {
  value: DashboardWindowDays
  onChange: (next: DashboardWindowDays) => void
}) {
  return (
    <fieldset className="inline-flex h-8 items-center rounded-md border border-input bg-muted p-0.5">
      <legend className="sr-only">Okres</legend>
      {DASHBOARD_WINDOWS.map((days) => {
        const chosen = value === days
        return (
          <label
            key={days}
            className={cn(
              "flex h-full cursor-pointer items-center rounded px-3 text-[13px] tabular-nums transition-colors has-focus-visible:ring-2 has-focus-visible:ring-ring",
              chosen
                ? "bg-card font-medium text-foreground shadow-xs"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <input
              type="radio"
              name="dashboard-window"
              value={days}
              checked={chosen}
              className="sr-only"
              onChange={() => onChange(days)}
            />
            {days} dni
          </label>
        )
      })}
    </fieldset>
  )
}

/** The word for how a reason acted, beside its code. */
const KIND: Record<DashboardReason["kind"], string> = {
  VETO: "weto",
  UNAVAILABLE: "brak danych",
  OVERRIDE: "odstępstwo",
  SCORE: "punkty",
}

/**
 * What stood behind the prepayments: a veto, a source that could not answer
 * for a rule that fails closed, an override, or points that counted.
 *
 * **Named by what was found, not by who was silent.** A veto is called by its
 * rule — the rule names the finding. An unavailability is called by its code —
 * "the source did not answer" — with the rule it left blind beside it, because
 * a rule's title would read as though the thing it looks for had been found.
 */
function whyPrepayment(snapshot: DashboardSnapshot, registry: Registry | undefined): RankedRow[] {
  return snapshot.reasons
    .filter((reason) => reason.prepayments > 0)
    .sort((left, right) => right.prepayments - left.prepayments)
    .map((reason) => {
      const rule = ruleTitle(registry, reason.ruleId)
      const described = codeDescription(registry, reason.code)
      const byRule = (reason.kind === "VETO" || reason.kind === "SCORE") && rule !== null
      return {
        key: `${reason.code}|${reason.kind}|${reason.ruleId ?? ""}`,
        label: byRule ? rule : (described ?? reason.code),
        detail: (
          <>
            <span className="font-mono">{reason.code}</span> · {KIND[reason.kind]}
            {!byRule && rule !== null && <> · {rule}</>}
          </>
        ),
        value: reason.prepayments,
        share: share(reason.prepayments, snapshot.totals.prepayment),
        to: `/decisions?decision=PREPAYMENT&code=${encodeURIComponent(reason.code)}`,
        ...(described === null ? {} : { title: described }),
      }
    })
}

/**
 * What the scoring rules recorded, whether or not it counted. While a rule
 * sits at weight 0 this is the only place its findings add up to anything —
 * the evidence the weight will one day be set from.
 */
function findings(snapshot: DashboardSnapshot, registry: Registry | undefined): RankedRow[] {
  return snapshot.reasons
    .filter((reason) => reason.kind === "SCORE")
    .sort((left, right) => right.decisions - left.decisions)
    .map((reason) => {
      const described = codeDescription(registry, reason.code)
      return {
        key: `${reason.code}|${reason.ruleId ?? ""}`,
        label: ruleTitle(registry, reason.ruleId) ?? described ?? reason.code,
        detail: (
          <>
            <span className="font-mono">{reason.code}</span> ·{" "}
            {reason.maxPoints === 0 ? "waga 0" : `do ${reason.maxPoints} pkt`} ·{" "}
            {count(reason.taxpayers)} {plural(reason.taxpayers, "podmiot", "podmioty", "podmiotów")}
          </>
        ),
        value: reason.decisions,
        share: share(reason.decisions, snapshot.totals.decisions),
        to: `/decisions?code=${encodeURIComponent(reason.code)}`,
        ...(described === null ? {} : { title: described }),
      }
    })
}

function ruleTitle(registry: Registry | undefined, ruleId: string | null): string | null {
  if (ruleId === null) return null
  return registry?.rules.find((rule) => rule.ruleId === ruleId)?.title ?? null
}

function codeDescription(registry: Registry | undefined, code: string): string | null {
  return registry?.reasonCodes.find((reason) => reason.code === code)?.description ?? null
}
